import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { 
  Download,
  Edit,
  Save,
  X,
  FileText,
  Image as ImageIcon,
  File
} from 'lucide-react';

// Import specialized viewers
import ImageViewer from './ImageViewer';
import WordViewerNew from './WordViewerNew';
import ExcelViewer from './ExcelViewer';
import CodeViewer from './CodeViewer';
import PDFViewer from './PDFViewer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FilePreviewDialog = ({ isOpen, onClose, file, projectId, myProfile, onFileUpdate }) => {
  const [fileContent, setFileContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen && file) {
      loadFileContent();
    }
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [isOpen, file]);

  const loadFileContent = async () => {
    if (!file || !myProfile) return;

    setLoading(true);
    try {
      const fileType = file.file_type.toLowerCase();
      const previewUrl = `${BACKEND_URL}/api/projects/${projectId}/files/${file.id}/preview?user_id=${myProfile.id}`;
      
      // Use XMLHttpRequest to bypass fetch interceptors that consume response body
      const xhr = new XMLHttpRequest();
      xhr.open('GET', previewUrl, true);
      
      // Set response type based on file type
      if (['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'mp3', 'wav', 'ogg', 'aac', 'm4a', 'mp4', 'webm', 'avi', 'mov', 'mkv'].includes(fileType)) {
        xhr.responseType = 'blob';
      } else {
        xhr.responseType = 'text';
      }
      
      xhr.onload = function() {
        if (xhr.status === 200) {
          if (xhr.responseType === 'blob') {
            const url = URL.createObjectURL(xhr.response);
            setPreviewUrl(url);
          } else {
            setFileContent(xhr.response);
            setEditContent(xhr.response);
          }
        } else if (xhr.status === 400) {
          setFileContent('This file cannot be previewed due to encoding issues. Please download to view.');
        } else {
          setFileContent('Failed to load file content. Please try downloading the file.');
        }
        setLoading(false);
      };
      
      xhr.onerror = function() {
        console.error('Error loading file');
        setFileContent('Error loading file. Please check your connection and try again.');
        setLoading(false);
      };
      
      xhr.send();
    } catch (error) {
      console.error('Error loading file:', error);
      setFileContent('Error loading file. Please check your connection and try again.');
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!file || !myProfile) return;

    setLoading(true);
    try {
      // Use the new direct content update endpoint
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}/files/${file.id}/content?user_id=${myProfile.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content: editContent })
      });

      if (response.ok) {
        const result = await response.json();
        setFileContent(editContent);
        setIsEditing(false);
        onFileUpdate && onFileUpdate();
        
        // Show success toast
        toast({
          title: "File Saved",
          description: `Updated ${file.original_filename} (v${result.version})`,
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to save file');
      }
    } catch (error) {
      console.error('Error saving file:', error);

      toast({
        title: "Save Failed",
        description: error.message || "Failed to save file changes",
        variant: "destructive"
      });

    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!file || !myProfile) return;

    try {
      // Use direct link download to bypass fetch interceptors that consume response body
      const downloadUrl = `${BACKEND_URL}/api/projects/${projectId}/files/${file.id}/download?user_id=${myProfile.id}`;
      
      // Create a temporary anchor element for direct download
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = downloadUrl;
      a.download = file.original_filename;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
      toast({
        title: "Download Failed",
        description: error.message || "Unable to download the file. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Helper function to determine file category
  const getFileCategory = () => {
    const fileType = file.file_type.toLowerCase();
    
    // Code files
    const codeExtensions = ['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp', 'c', 'cs', 'php', 'rb', 'go', 'rs', 
                           'swift', 'kt', 'scala', 'html', 'css', 'scss', 'sass', 'less', 'json', 'xml', 
                           'yaml', 'yml', 'sql', 'sh', 'bash', 'dockerfile', 'r', 'matlab', 'm'];
    
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(fileType)) return 'image';
    if (['doc', 'docx'].includes(fileType)) return 'word';
    if (['xlsx', 'xls', 'csv'].includes(fileType)) return 'excel';
    if (codeExtensions.includes(fileType)) return 'code';
    if (fileType === 'pdf') return 'pdf';
    if (['txt', 'md'].includes(fileType)) return 'text';
    if (['mp3', 'wav', 'ogg', 'aac', 'm4a'].includes(fileType)) return 'audio';
    if (['mp4', 'webm', 'avi', 'mov', 'mkv'].includes(fileType)) return 'video';
    
    return 'other';
  };

  // Save handler for specialized viewers
  const handleViewerSave = async (content) => {
    if (!file || !myProfile || !content) {
      const missing = [];
      if (!file) missing.push('file');
      if (!myProfile) missing.push('user profile'); 
      if (!content) missing.push('content');
      
      toast({
        title: "Save Failed",
        description: `Missing: ${missing.join(', ')}. Please refresh and try again.`,
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}/files/${file.id}/content?user_id=${myProfile.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content: content })
      });

      if (response.ok) {
        // IMMEDIATELY update the file content so it appears right away
        setFileContent(content);
        
        // Trigger parent component refresh
        if (onFileUpdate) {
          onFileUpdate();
        }
        
        // Show success message
        toast({
          title: "✅ File Saved!",
          description: `Successfully updated ${file.original_filename}`,
        });
        
      } else {
        if (response.status === 401) {
          toast({
            title: "❌ Authentication Failed",
            description: "Please log out and log back in.",
            variant: "destructive"
          });
        } else if (response.status === 403) {
          toast({
            title: "❌ Access Denied", 
            description: "You don't have permission to edit this file.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "❌ Save Failed",
            description: `Server error (${response.status}). Please try again.`,
            variant: "destructive"
          });
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      
      toast({
        title: "❌ Save Failed", 
        description: "Network error. Please check your connection and try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Save handler for images (blob-based upload)
  const handleImageSave = async (imageBlob) => {
    if (!file || !myProfile || !imageBlob) {
      toast({
        title: "Save Failed",
        description: "Missing required data to save image",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    try {
      // Convert blob to base64 for storage
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64data = reader.result;
        
        // Upload the edited image as a file replacement
        const formData = new FormData();
        formData.append('file', imageBlob, file.original_filename);
        formData.append('user_id', myProfile.id);

        try {
          // First, delete the old file
          await fetch(`${BACKEND_URL}/api/projects/${projectId}/files/${file.id}?user_id=${myProfile.id}`, {
            method: 'DELETE'
          });

          // Then upload the new edited version
          const uploadResponse = await fetch(`${BACKEND_URL}/api/projects/${projectId}/files?user_id=${myProfile.id}`, {
            method: 'POST',
            body: formData
          });

          if (uploadResponse.ok) {
            toast({
              title: "✅ Image Saved!",
              description: `Successfully updated ${file.original_filename}`,
            });
            
            // Trigger parent component refresh to show updated file
            if (onFileUpdate) {
              onFileUpdate();
            }
            
            // Reload the file content to show the saved version
            loadFileContent();
          } else {
            throw new Error('Failed to upload edited image');
          }
        } catch (uploadError) {
          console.error('Upload error:', uploadError);
          toast({
            title: "❌ Save Failed",
            description: "Failed to upload edited image",
            variant: "destructive"
          });
        } finally {
          setLoading(false);
        }
      };
      
      reader.readAsDataURL(imageBlob);
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "❌ Save Failed",
        description: "Failed to process edited image",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const renderFilePreview = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
          <p className="ml-3 text-gray-600">Loading file...</p>
        </div>
      );
    }

    const fileCategory = getFileCategory();

    // Route to specialized viewers based on file category
    switch (fileCategory) {
      case 'image':
        return (
          <ImageViewer
            file={file}
            previewUrl={previewUrl}
            onDownload={handleDownload}
            onSave={handleImageSave}
            isLoading={loading}
          />
        );

      case 'word':
        return (
          <WordViewerNew
            file={file}
            content={fileContent}
            projectId={projectId}
            myProfile={myProfile}
            onSave={handleViewerSave}
            onDownload={handleDownload}
            onClose={onClose}
            isLoading={loading}
          />
        );

      case 'excel':
        return (
          <ExcelViewer
            file={file}
            content={fileContent}
            projectId={projectId}
            myProfile={myProfile}
            onSave={handleViewerSave}
            onDownload={handleDownload}
            isLoading={loading}
          />
        );

      case 'code':
        return (
          <CodeViewer
            file={file}
            content={fileContent}
            projectId={projectId}
            myProfile={myProfile}
            onSave={handleViewerSave}
            onDownload={handleDownload}
            isLoading={loading}
          />
        );

      case 'pdf':
        return (
          <PDFViewer
            file={file}
            content={fileContent}
            projectId={projectId}
            myProfile={myProfile}
            onDownload={handleDownload}
            previewUrl={previewUrl}
          />
        );

      case 'audio':
        return (
          <div className="space-y-4">
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-purple-100 mb-4">
                <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
              </div>
              <h3 className="font-medium text-lg mb-2">{file.original_filename}</h3>
              <div className="flex justify-center">
                <audio 
                  controls 
                  className="w-full max-w-2xl"
                  preload="metadata"
                  src={previewUrl}
                >
                  Your browser does not support the audio element.
                </audio>
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <p>Size: {Math.round(file.file_size / 1024)} KB</p>
                <p>Uploaded: {new Date(file.uploaded_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        );

      case 'video':
        return (
          <div className="space-y-4">
            <div className="text-center py-8">
              <h3 className="font-medium text-lg mb-4">{file.original_filename}</h3>
              <div className="flex justify-center">
                <video 
                  controls 
                  className="w-full max-w-4xl rounded-lg shadow-lg"
                  preload="metadata"
                  src={previewUrl}
                  style={{ maxHeight: '500px' }}
                >
                  Your browser does not support the video element.
                </video>
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <p>Size: {Math.round(file.file_size / 1024)} KB</p>
                <p>Uploaded: {new Date(file.uploaded_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        );

      case 'text':
        // Keep the existing textarea implementation for simple text files
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">
                {isEditing ? 'Editing File' : 'File Content'}
                <span className="text-sm text-gray-500 ml-2">
                  {fileContent.length} characters
                </span>
              </h3>
              <div className="flex gap-2">
                {!isEditing ? (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setIsEditing(true)}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setIsEditing(false);
                        setEditContent(fileContent);
                      }}
                      disabled={loading}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Cancel
                    </Button>
                    <Button 
                      size="sm"
                      onClick={handleSave}
                      disabled={loading || editContent === fileContent}
                      className="bg-cyan-500 hover:bg-cyan-600"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-1" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>
            
            {isEditing ? (
              <div className="space-y-2">
                <Textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="min-h-[400px] font-mono text-sm resize-none"
                  placeholder="Edit file content..."
                  autoFocus
                />
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{editContent.length} characters</span>
                  <span className={editContent !== fileContent ? 'text-orange-600 font-medium' : ''}>
                    {editContent !== fileContent ? 'Unsaved changes' : 'No changes'}
                  </span>
                </div>
              </div>
            ) : (
              <div className="border rounded-md bg-gray-50">
                <div className="p-4 max-h-[400px] overflow-auto">
                  <pre className="whitespace-pre-wrap text-sm font-mono leading-relaxed">{fileContent}</pre>
                </div>
                <div className="px-4 py-2 border-t bg-gray-100 text-xs text-gray-600 flex items-center justify-between">
                  <span>Click "Edit" to modify this file</span>
                  <span>Read-only view</span>
                </div>
              </div>
            )}
          </div>
        );

      default:
        // For unsupported file types, show file information
        return (
          <div className="text-center py-8">
            <File className="h-16 w-16 mx-auto mb-4 text-gray-400" />
            <div className="space-y-3">
              <p className="text-gray-700 font-medium">File Information</p>
              <div className="bg-gray-50 p-4 rounded-lg text-left max-w-md mx-auto">
                <div className="space-y-2 text-sm">
                  <div><span className="font-medium">Name:</span> {file.original_filename}</div>
                  <div><span className="font-medium">Type:</span> {file.file_type.toUpperCase()}</div>
                  <div><span className="font-medium">Size:</span> {Math.round(file.file_size / 1024)} KB</div>
                  <div><span className="font-medium">Uploaded:</span> {new Date(file.uploaded_at).toLocaleDateString()}</div>
                </div>
              </div>
              {fileContent && (
                <p className="text-gray-600 text-sm mt-4">{fileContent}</p>
              )}
              <p className="text-gray-500 text-sm">This file type doesn't support preview. Click download to save it.</p>
            </div>
          </div>
        );
    }
  };

  const getFileIcon = () => {
    if (['png', 'jpg', 'jpeg', 'gif'].includes(file?.file_type)) {
      return <ImageIcon className="h-6 w-6 text-blue-600" />;
    }
    if (file?.file_type === 'pdf') {
      return <FileText className="h-6 w-6 text-red-600" />;
    }
    return <File className="h-6 w-6 text-gray-600" />;
  };

  if (!file) return null;

  const fileCategory = getFileCategory();
  const showSimpleHeader = ['image', 'word', 'excel', 'code', 'pdf', 'audio', 'video'].includes(fileCategory);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
        {!showSimpleHeader && (
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {getFileIcon()}
              {file.original_filename}
            </DialogTitle>
            <DialogDescription className="flex items-center justify-between">
              <div>
                <span>Size: {Math.round(file.file_size / 1024)} KB</span>
                <span className="mx-2">•</span>
                <span>Uploaded by: {file.uploaded_by_name}</span>
                <span className="mx-2">•</span>
                <span>{new Date(file.uploaded_at).toLocaleDateString()}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                className="ml-4"
              >
                <Download className="h-4 w-4 mr-1" />
                Download
              </Button>
            </DialogDescription>
          </DialogHeader>
        )}
        
        <div className={showSimpleHeader ? '' : 'mt-4'}>
          {renderFilePreview()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FilePreviewDialog;